import {
  dataTableHeadClass,
  DataTablePanel,
} from '../../components/DataTablePanel';
import { Card } from '../../components/ui';
import { t } from '../../i18n';
import type { MydataYearMonthSummary, MydataYearSummary } from '../../types/mydata';
import { formatMoney } from '../../utils/format';

function toNum(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function meaningLabel(
  labels: ReturnType<typeof t>['mydata'],
  amount: number,
  synced: boolean,
) {
  if (!synced) return labels.debtsMonthNotSynced;
  if (amount === 0) return labels.debtsMonthZero;
  return amount < 0 ? labels.vatRefundable : labels.vatPayable;
}

function VatBreakdownTable({
  periodHeader,
  rows,
  footer,
  selectedKey,
  onSelect,
}: {
  periodHeader: string;
  rows: Array<{
    key: string | number;
    label: string;
    sublabel?: string;
    synced: boolean;
    selectable: boolean;
    outputVat: number;
    inputVat: number;
    amount: number;
  }>;
  footer: {
    outputVat: number;
    inputVat: number;
    amount: number;
  } | null;
  selectedKey: string | number | null;
  onSelect: (key: string | number) => void;
}) {
  const labels = t().mydata;

  return (
    <DataTablePanel>
      <table className="min-w-full text-sm">
        <thead className={dataTableHeadClass}>
          <tr>
            <th className="px-3 py-2 text-left font-medium">{periodHeader}</th>
            <th className="px-3 py-2 text-right font-medium">
              {labels.debtsColOutputVat}
            </th>
            <th className="px-3 py-2 text-right font-medium">
              {labels.debtsColInputVat}
            </th>
            <th className="px-3 py-2 text-right font-medium">
              {labels.debtsColDue}
            </th>
            <th className="px-3 py-2 text-left font-medium">
              {labels.debtsColMeaning}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = selectedKey === row.key;
            return (
              <tr
                key={row.key}
                className={`border-t border-line ${
                  selected ? 'bg-brand-soft/60' : ''
                } ${
                  row.selectable
                    ? 'cursor-pointer hover:bg-surface-muted'
                    : 'opacity-60'
                }`}
                onClick={() => {
                  if (row.selectable) onSelect(row.key);
                }}
              >
                <td className="px-3 py-2 font-medium text-ink">
                  <div>{row.label}</div>
                  {row.sublabel ? (
                    <div className="text-xs font-normal text-ink-faint">
                      {row.sublabel}
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
                  {row.synced ? formatMoney(row.outputVat.toFixed(2)) : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
                  {row.synced ? formatMoney(row.inputVat.toFixed(2)) : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-ink">
                  {row.synced
                    ? formatMoney(Math.abs(row.amount).toFixed(2))
                    : '—'}
                </td>
                <td className="px-3 py-2 text-ink-muted">
                  {meaningLabel(labels, row.amount, row.synced)}
                </td>
              </tr>
            );
          })}
        </tbody>
        {footer ? (
          <tfoot>
            <tr className="border-t-2 border-line bg-surface-muted/40 font-semibold">
              <td className="px-3 py-2.5 text-ink">{labels.totals}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                {formatMoney(footer.outputVat.toFixed(2))}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                {formatMoney(footer.inputVat.toFixed(2))}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                {formatMoney(Math.abs(footer.amount).toFixed(2))}
              </td>
              <td className="px-3 py-2.5 text-ink">
                {meaningLabel(labels, footer.amount, true)}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </DataTablePanel>
  );
}

function buildQuarterRows(
  months: MydataYearMonthSummary[],
  labels: ReturnType<typeof t>['mydata'],
  monthNames: readonly string[],
) {
  return [1, 2, 3, 4].map((quarter) => {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    const quarterMonths = months.filter(
      (m) => m.month >= startMonth && m.month <= endMonth,
    );
    const synced = quarterMonths.filter((m) => m.synced);
    const outputVat = synced.reduce((acc, m) => acc + toNum(m.outputVat), 0);
    const inputVat = synced.reduce((acc, m) => acc + toNum(m.inputVat), 0);
    const amount =
      Math.round(
        synced.reduce((acc, m) => acc + toNum(m.vatPayable), 0) * 100,
      ) / 100;
    const fromName = monthNames[startMonth - 1] ?? String(startMonth);
    const toName = monthNames[endMonth - 1] ?? String(endMonth);

    let sublabel = labels.debtsQuarterMonths
      .replace('{from}', fromName)
      .replace('{to}', toName);
    if (synced.length === 0) {
      sublabel = labels.debtsQuarterEmpty;
    } else if (synced.length < 3) {
      sublabel = `${sublabel} · ${labels.debtsQuarterPartial.replace(
        '{synced}',
        String(synced.length),
      )}`;
    }

    return {
      key: quarter,
      label: labels.debtsQuarterLabel.replace('{n}', String(quarter)),
      sublabel,
      synced: synced.length > 0,
      selectable: synced.length > 0,
      outputVat: Math.round(outputVat * 100) / 100,
      inputVat: Math.round(inputVat * 100) / 100,
      amount,
    };
  });
}

export function MydataDebtsYearTable({
  yearSummary,
  selectedMonth,
  selectedQuarter,
  onSelectMonth,
  onSelectQuarter,
}: {
  yearSummary: MydataYearSummary | null | undefined;
  selectedMonth: number | null;
  selectedQuarter: number | null;
  onSelectMonth: (month: number) => void;
  onSelectQuarter: (quarter: number) => void;
}) {
  const labels = t().mydata;
  const monthNames = labels.monthNamesShort;

  if (!yearSummary) return null;

  const months = yearSummary.months ?? [];
  const syncedMonths = months.filter((m) => m.synced);
  const outputTotal = syncedMonths.reduce(
    (acc, m) => acc + toNum(m.outputVat),
    0,
  );
  const inputTotal = syncedMonths.reduce(
    (acc, m) => acc + toNum(m.inputVat),
    0,
  );
  const netFromMonths = syncedMonths.reduce(
    (acc, m) => acc + toNum(m.vatPayable),
    0,
  );
  const payableTotal = syncedMonths.reduce((acc, m) => {
    const v = toNum(m.vatPayable);
    return v > 0 ? acc + v : acc;
  }, 0);
  const refundTotal = syncedMonths.reduce((acc, m) => {
    const v = toNum(m.vatPayable);
    return v < 0 ? acc + Math.abs(v) : acc;
  }, 0);
  const yearNet = toNum(yearSummary.vatPayable);
  const yearIsRefund = yearNet < 0;
  const footerNet = Math.round(netFromMonths * 100) / 100;

  const monthRows = months.map((row) => ({
    key: row.month,
    label: monthNames[row.month - 1] ?? String(row.month),
    synced: row.synced,
    selectable: row.synced,
    outputVat: toNum(row.outputVat),
    inputVat: toNum(row.inputVat),
    amount: toNum(row.vatPayable),
  }));

  const quarterRows = buildQuarterRows(months, labels, monthNames);

  return (
    <section className="shrink-0 space-y-5">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-ink">
          {labels.debtsYearTitle} {yearSummary.year}
        </h2>
        <p className="text-xs leading-relaxed text-ink-faint">
          {labels.debtsYearHint}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="space-y-1 p-3">
          <div className="text-xs text-ink-faint">{labels.debtsYearPayableTotal}</div>
          <div className="text-lg font-semibold tabular-nums text-ink">
            {formatMoney(payableTotal.toFixed(2))}
          </div>
        </Card>
        <Card className="space-y-1 p-3">
          <div className="text-xs text-ink-faint">{labels.debtsYearRefundTotal}</div>
          <div className="text-lg font-semibold tabular-nums text-ink">
            {formatMoney(refundTotal.toFixed(2))}
          </div>
        </Card>
        <Card className="space-y-1 border-brand/30 bg-brand-soft p-3">
          <div className="text-xs text-brand">
            {yearIsRefund ? labels.vatRefundable : labels.vatPayable}
          </div>
          <div className="text-lg font-semibold tabular-nums text-brand">
            {formatMoney(Math.abs(yearNet).toFixed(2))}
          </div>
        </Card>
      </div>

      {syncedMonths.length === 0 ? (
        <p className="text-sm text-ink-faint">{labels.debtsYearEmpty}</p>
      ) : (
        <>
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-ink">{labels.month}</h3>
            <VatBreakdownTable
              periodHeader={labels.month}
              rows={monthRows}
              footer={{
                outputVat: Math.round(outputTotal * 100) / 100,
                inputVat: Math.round(inputTotal * 100) / 100,
                amount: footerNet,
              }}
              selectedKey={selectedMonth}
              onSelect={(key) => onSelectMonth(Number(key))}
            />
          </div>

          <div className="space-y-2">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-ink">
                {labels.debtsQuarterTitle}
              </h3>
              <p className="text-xs text-ink-faint">{labels.debtsQuarterHint}</p>
            </div>
            <VatBreakdownTable
              periodHeader={labels.quarter}
              rows={quarterRows}
              footer={{
                outputVat: Math.round(outputTotal * 100) / 100,
                inputVat: Math.round(inputTotal * 100) / 100,
                amount: footerNet,
              }}
              selectedKey={selectedQuarter}
              onSelect={(key) => onSelectQuarter(Number(key))}
            />
          </div>
        </>
      )}
    </section>
  );
}
